import React, { useState } from 'react';
import { Home, TrendingUp, Lightbulb, Settings, ChevronRight, LogOut, CalendarDays, Users, FileText } from 'lucide-react';
import { DjayNarrator } from '../../mascot';
export type ParentV2Route = 'home' | 'voortgang' | 'toetsen' | 'tips' | 'activiteit' | 'compare' | 'settings';
interface NavItem {
  id: ParentV2Route;
  label: string;
  icon: React.ReactNode;
  /** Optional minimum number of linked children required for this item to render. */
  minChildren?: number;
}
const NAV_ITEMS: NavItem[] = [{
  id: 'home',
  label: 'Overzicht',
  icon: <Home className="w-5 h-5" data-id="element-9102" data-mp="1hr3g2anro6lw" />
}, {
  id: 'voortgang',
  label: 'Vakken',
  icon: <TrendingUp className="w-5 h-5" data-id="element-9103" data-mp="1cpw8ocpjd9fr" />
}, {
  id: 'toetsen',
  label: 'Toetsen',
  icon: <FileText className="w-5 h-5" data-id="element-9104" data-mp="17runnm8jv01n" />
}, {
  id: 'tips',
  label: 'Tips',
  icon: <Lightbulb className="w-5 h-5" data-id="element-9105" data-mp="373x5ks1izmd7" />
}, {
  id: 'activiteit',
  label: 'Activiteit',
  icon: <CalendarDays className="w-5 h-5" data-id="element-9106" data-mp="3242g8sxef4u9" />
}, {
  id: 'compare',
  label: 'Vergelijken',
  icon: <Users className="w-5 h-5" data-id="element-9107" data-mp="x5zotm92bxtf" />,
  minChildren: 2
}];
interface ParentSidebarV2Props {
  activeRoute: ParentV2Route;
  onNavigate: (route: ParentV2Route) => void;
  onLogout?: () => void;
  parentName?: string;
  linkedLearnersCount?: number;
}
/**
 * Parent portal sidebar.
 *
 * Wave 1 (F-SHL-1): The child selector was removed from this sidebar.
 * The page-level `ChildSelectorBar` (pill row) is now the single source of
 * truth for multi-child switching. This avoids two controls competing for
 * the same state.
 */
export function ParentSidebarV2({
  activeRoute,
  onNavigate,
  onLogout,
  parentName = 'Ouder',
  linkedLearnersCount = 0
}: ParentSidebarV2Props) {
  const [collapsed, setCollapsed] = useState(false);
  return <aside className={`flex flex-col bg-white border-r border-slate-200 transition-all duration-300 flex-shrink-0 h-full ${collapsed ? 'w-20' : 'w-64'}`} data-id="element-9108" data-mp="1b4hcjjyyxabc">
      
      {/* Header / Logo */}
      <div className={`flex items-center border-b border-slate-100 flex-shrink-0 h-20 ${collapsed ? 'justify-center px-2' : 'justify-between px-6'}`} data-id="element-9109" data-mp="23mvarko7uyay">
        
        {!collapsed && <div className="flex items-center gap-3" data-id="element-9110" data-mp="28iz3vcwjz2nv">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100 shadow-sm overflow-hidden" data-id="element-9111" data-mp="28jij2lutjcy7">
              <DjayNarrator size="xs" emotion="happy" data-id="element-9112" data-mp="1zikf23kdr5fm" />
            </div>
            <div className="flex flex-col" data-id="element-9113" data-mp="1zkdp7su7c7sd">
              <span className="text-sm font-black text-slate-900 leading-tight tracking-tight" data-id="element-9114" data-mp="1ujzn0rfa7s29">
                Meester djay
              </span>
              <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider" data-id="element-9115" data-mp="1pqemosq7ulzw">
                Ouderportaal
              </span>
            </div>
          </div>}
        {collapsed && <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100 shadow-sm overflow-hidden" data-id="element-9116" data-mp="1fryusnjd78pc">
            <DjayNarrator size="xs" emotion="happy" data-id="element-9117" data-mp="16t3olbgblym1" />
          </div>}
        {!collapsed && <button onClick={() => setCollapsed(true)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none" aria-label="Inklappen" data-id="element-9118" data-mp="3igz2fwygwp3l">
          
            <ChevronRight className="w-4 h-4 rotate-180" data-id="element-9119" data-mp="3rfapxq41f6rt" />
          </button>}
      </div>

      {/* Parent Info */}
      {!collapsed && <div className="px-6 py-4 border-b border-slate-100" data-id="element-9120" data-mp="2zqhf67wemlpm">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-9121" data-mp="2zs25flboriho">
            Ingelogd als
          </div>
          <div className="text-sm font-bold text-slate-900 truncate" data-id="element-9122" data-mp="38r5posmla3xr">
            {parentName}
          </div>
        </div>}

      {/* 0 Children CTA */}
      {!collapsed && linkedLearnersCount === 0 && <div className="px-4 pt-4" data-id="element-9123" data-mp="2g2hzqjqwaqdv">
          <div className="p-4 rounded-xl border-2 border-dashed border-emerald-100 bg-emerald-50/50 flex flex-col items-center text-center" data-id="element-9124" data-mp="2g1grso56ek0d">
            <h3 className="text-sm font-bold text-slate-900 mb-1" data-id="element-9125" data-mp="2kyyqllphbiun">
              Nog geen kind gekoppeld
            </h3>
            <p className="text-xs text-slate-600 mb-3" data-id="element-9126" data-mp="mk1a3p84d0mi">
              Koppel je kind om voortgang te zien.
            </p>
            <button onClick={() => onNavigate('settings')} className="w-full py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 transition-colors shadow-sm" data-id="element-9127" data-mp="10bxx5euxkkc1">
            
              Kind koppelen
            </button>
          </div>
        </div>}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-6 px-3" data-id="element-9128" data-mp="158xvt4pgakt4">
        <div className="space-y-1.5" data-id="element-9129" data-mp="1j9s13eh7c5nk">
          {NAV_ITEMS.filter(item => !item.minChildren || linkedLearnersCount >= item.minChildren).map(item => {
          const isActive = activeRoute === item.id;
          return <button key={item.id} onClick={() => onNavigate(item.id)} title={collapsed ? item.label : undefined} className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 relative focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none ${collapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} ${isActive ? 'bg-emerald-50 text-emerald-700 font-bold shadow-sm border border-emerald-100/50' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium border border-transparent'}`} data-id="element-9130" data-mp="3l0viqnpiekhm">
                
                <span className={`flex-shrink-0 transition-colors ${isActive ? 'text-emerald-600' : 'text-slate-400 group-hover:text-slate-600'}`} data-id="element-9131" data-mp="3232102vzmrfl">
                  
                  {item.icon}
                </span>
                {!collapsed && <span className="text-sm truncate" data-id="element-9132" data-mp="2e6xpsuapwf23">{item.label}</span>}
              </button>;
        })}
        </div>
      </nav>

      {/* Footer */}
      <div className={`flex-shrink-0 border-t border-slate-100 ${collapsed ? 'p-3' : 'p-5'} space-y-2`} data-id="element-9133" data-mp="2j6tu6unbu8i6">
        
        {collapsed && <button onClick={() => setCollapsed(false)} className="w-full flex items-center justify-center p-2.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors mb-2" aria-label="Uitklappen" data-id="element-9134" data-mp="cdhydv6t2w62">
          
            <ChevronRight className="w-5 h-5" data-id="element-9135" data-mp="axvy22d272n2" />
          </button>}

        <button onClick={() => onNavigate('settings')} title={collapsed ? 'Instellingen' : undefined} className={`w-full flex items-center gap-3 rounded-xl transition-colors focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5'} ${activeRoute === 'settings' ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 font-medium'}`} data-id="element-9136" data-mp="b0rxjv4k1zdm">
          
          <Settings className={`w-5 h-5 ${activeRoute === 'settings' ? 'text-emerald-600' : ''}`} data-id="element-9137" data-mp="owlvjj3sj4by" />
          
          {!collapsed && <span className="text-sm" data-id="element-9138" data-mp="jwzfl1wkv9zg">Instellingen</span>}
        </button>

        {onLogout && <button onClick={onLogout} className={`w-full flex items-center gap-3 rounded-xl text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5 font-medium'}`} title={collapsed ? 'Uitloggen' : undefined} data-id="element-9139" data-mp="1hfskxcfil98l">
          
            <LogOut className="w-5 h-5" data-id="element-9140" data-mp="1twtsuij67sat" />
            {!collapsed && <span className="text-sm" data-id="element-9141" data-mp="1tzg1ymlpmfv5">Uitloggen</span>}
          </button>}

        {/*
         Hier stond "Leerlingomgeving": een stille accountwissel naar het
         account van een kind, zonder wachtwoord. Verwijderd — zie de
         toelichting in src/app/PortalSwitch.tsx.
         */}
      </div>
    </aside>;
}